import { z } from 'zod';
import { ConversationRunStatus, ConversationStatus } from '../../models';
import { aiAgentSettingsSchema } from '../request/aiAgent';
export const aiAgentConversationSchema = z.object({
    id: z.uuid(),
    organisation_id: z.uuid(),
    contact_id: z.number().int(),
    invoice_id: z.uuid().nullable(),
    invoice_number: z.string().nullable().optional(),
    source_action_id: z.uuid().nullable().optional(),
    status: z.enum(ConversationStatus),
    summary: z.string().nullable(),
    last_activity_at: z.string(),
    opened_at: z.string(),
    closed_at: z.string().nullable(),
    archived_at: z.string().nullable(),
    archived_by_user_id: z.uuid().nullable().optional(),
    contact_name: z.string().nullable().optional(),
    pending_task_count: z.number().int().optional(),
    failed_run_count: z.number().int().optional(),
});
export const aiAgentRunSchema = z.object({
    id: z.uuid(),
    conversation_id: z.uuid(),
    organisation_id: z.uuid(),
    trigger_type: z.string(),
    sender: z.string().nullable(),
    status: z.enum(ConversationRunStatus),
    error: z.string().nullable(),
    started_at: z.string().nullable(),
    finished_at: z.string().nullable(),
    inbound_sms_id: z.number().int().nullable(),
    inbound_email_id: z.uuid().nullable(),
    attachments: z.array(z.object({
        index: z.number().int(),
        name: z.string(),
    })).default([]),
    created_at: z.string(),
});
export const aiAgentTaskSchema = z.object({
    id: z.uuid(),
    run_id: z.uuid(),
    conversation_id: z.uuid(),
    action_id: z.uuid().nullable(),
    invoice_id: z.uuid().nullable(),
    quote_id: z.number().int().nullable(),
    tool_name: z.string(),
    args: z.unknown(),
    original_args: z.unknown().optional(),
    call_id: z.string(),
    requires_approval: z.boolean(),
    approval_status: z.string(),
    execution_status: z.string(),
    result: z.unknown(),
    error: z.string().nullable(),
    decided_by_user_id: z.uuid().nullable(),
    decided_at: z.string().nullable(),
    executed_at: z.string().nullable(),
    args_edited_by_user_id: z.uuid().nullable().optional(),
    args_edited_at: z.string().nullable().optional(),
    created_at: z.string(),
});
export const aiAgentMessageSchema = z.object({
    id: z.string(),
    conversation_id: z.uuid(),
    run_id: z.uuid().nullable(),
    item_type: z.string(),
    role: z.string().nullable(),
    content: z.string().nullable(),
    tool_name: z.string().nullable(),
    call_id: z.string().nullable(),
    arguments: z.unknown(),
    output: z.unknown(),
    created_at: z.string(),
});
export const aiAgentConversationNoteSchema = z.object({
    id: z.uuid(),
    conversation_id: z.uuid(),
    content: z.string(),
    author_user_id: z.uuid(),
    author_name: z.string().nullable(),
    mentions: z.array(z.object({
        id: z.uuid(),
        name: z.string().nullable(),
        email: z.string().nullable(),
    })),
    created_at: z.string(),
});
export const aiAgentToolApprovalSchema = z.object({
    id: z.uuid(),
    organisation_id: z.uuid(),
    tool_name: z.string(),
    auto_approve: z.boolean(),
    created_at: z.string(),
    updated_at: z.string(),
});
export const aiAgentPromptSchema = z.object({
    id: z.uuid(),
    organisation_id: z.uuid(),
    title: z.string(),
    instruction: z.string(),
    enabled: z.boolean(),
    position: z.number().int(),
    created_at: z.string(),
    updated_at: z.string(),
});
export const aiAgentConversationOriginalOutboundMessageSchema = z.union([
    z.object({
        channel: z.literal(`email`),
        subject: z.string().nullable(),
        html: z.string().nullable(),
        to: z.string().nullable(),
        sentAt: z.string().nullable(),
    }),
    z.object({
        channel: z.literal(`sms`),
        body: z.string().nullable(),
        to: z.string().nullable(),
        sentAt: z.string().nullable(),
    }),
]);
export const aiAgentConversationsResponseSchema = z.object({
    conversations: z.array(aiAgentConversationSchema),
    pagination: z.object({
        page: z.number().int(),
        pageSize: z.number().int(),
        totalPages: z.number().int(),
        totalCount: z.number().int(),
    }),
});
export const aiAgentConversationResponseSchema = z.object({
    conversation: aiAgentConversationSchema,
    runs: z.array(aiAgentRunSchema),
    tasks: z.array(aiAgentTaskSchema),
    transcript: z.array(aiAgentMessageSchema),
    notes: z.array(aiAgentConversationNoteSchema).default([]),
    originalOutboundMessage: aiAgentConversationOriginalOutboundMessageSchema.nullable().optional(),
});
export const aiAgentTaskDecisionResponseSchema = z.object({
    queued: z.boolean(),
    taskId: z.uuid(),
});
export const aiAgentCheckResolvedResponseSchema = z.object({
    queued: z.boolean(),
    runId: z.uuid(),
});
export const aiAgentSendMessageResponseSchema = z.object({
    queued: z.boolean(),
    runId: z.uuid(),
    messageId: z.string(),
});
export const aiAgentResolveConversationResponseSchema = z.object({
    ok: z.boolean(),
});
export const aiAgentArchiveConversationResponseSchema = z.object({
    conversation: aiAgentConversationSchema,
});
export const aiAgentToolApprovalsResponseSchema = z.object({
    preferences: z.array(aiAgentToolApprovalSchema),
});
export const aiAgentToolApprovalResponseSchema = z.object({
    preference: aiAgentToolApprovalSchema,
});
export const aiAgentPromptsResponseSchema = z.object({
    prompts: z.array(aiAgentPromptSchema),
});
export const aiAgentPromptResponseSchema = z.object({
    prompt: aiAgentPromptSchema,
});
export const aiAgentDeletePromptResponseSchema = z.object({
    deleted: z.number().int(),
});
export const aiAgentSettingsResponseSchema = aiAgentSettingsSchema;
//# sourceMappingURL=aiAgent.js.map